import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ShieldCheck, TrendingUp, Users, PlayCircle, CheckCircle2, MousePointerClick, Loader2, AlertTriangle } from "lucide-react";

function pct(v) {
  if (v === null || v === undefined) return "0%";
  return `${v}%`;
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="pm-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
        <Icon size={12} /> {label}
      </div>
      <div className="font-display font-bold text-3xl">{value}</div>
      {sub && (
        <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{sub}</div>
      )}
    </div>
  );
}

function FunnelBar({ label, value, max, color, testid }) {
  const pctFill = max ? Math.max(6, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="w-32 shrink-0 text-sm font-semibold">{label}</div>
      <div className="flex-1 h-8 rounded-lg overflow-hidden" style={{ background: "var(--muted)" }}>
        <div
          className="h-full flex items-center px-3 text-sm font-semibold text-white transition-all"
          style={{ width: `${pctFill}%`, background: color }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

export default function PlaygroundAnalytics() {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api
      .get("/admin/playground-analytics")
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 403) setDenied(true);
      });
  }, []);

  if (denied) {
    return (
      <div className="max-w-md mx-auto pm-card p-8 text-center mt-16" data-testid="pg-analytics-denied">
        <AlertTriangle size={40} className="mx-auto mb-3" style={{ color: "var(--weakness)" }} />
        <h1 className="font-display text-2xl font-bold mb-2">Admin only</h1>
        <p style={{ color: "var(--text-secondary)" }}>
          Analytics are locked to the PictureMed founder account.
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center gap-2 py-16 justify-center" data-testid="pg-analytics-loading">
        <Loader2 className="animate-spin" size={18} /> Crunching numbers…
      </div>
    );
  }

  const f = data.funnel;
  const empty = f.opens === 0;

  return (
    <div className="max-w-6xl mx-auto" data-testid="pg-analytics-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="pm-badge mb-2" style={{ background: "var(--hl-blue)", color: "var(--primary)" }}>
            <ShieldCheck size={12} className="mr-1.5" /> Admin · Playground
          </div>
          <h1 className="font-display text-4xl font-bold">Playground Analytics</h1>
          <p className="mt-2" style={{ color: "var(--text-secondary)" }}>
            Anonymous funnel from Landing → analogy tour → signup. Every session is deduped by a local session id.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/admin/feedback" className="pm-btn-secondary text-sm" data-testid="pg-back-to-feedback">
            ← Feedback inbox
          </Link>
        </div>
      </div>

      {empty ? (
        <div className="pm-card p-8 text-center" data-testid="pg-analytics-empty">
          <div className="text-3xl mb-2">📊</div>
          <h2 className="font-display text-2xl font-bold mb-1">No sessions tracked yet</h2>
          <p style={{ color: "var(--text-secondary)" }}>
            Once an anonymous visitor opens the playground on the landing page, the funnel populates here in real time.
          </p>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <StatCard icon={PlayCircle} label="Opens" value={f.opens} sub="unique sessions" testid="pg-stat-opens" />
            <StatCard icon={CheckCircle2} label="Completions" value={f.completions} sub={`${pct(f.completion_rate)} of opens`} testid="pg-stat-completions" />
            <StatCard icon={MousePointerClick} label="Signup clicks" value={f.signup_clicks} sub={`${pct(f.signup_rate)} conversion`} testid="pg-stat-signup" />
            <StatCard icon={Users} label="Total events" value={Object.values(data.totals).reduce((a, b) => a + b, 0)} sub="every stage view" testid="pg-stat-events" />
          </div>

          {/* Funnel bars */}
          <div className="pm-card p-6 mb-6" data-testid="pg-funnel">
            <h2 className="font-display font-bold text-xl mb-4 inline-flex items-center gap-2">
              <TrendingUp size={16} /> Conversion funnel
            </h2>
            <div className="space-y-3">
              <FunnelBar label="Opens" value={f.opens} max={f.opens} color="var(--primary)" testid="pg-funnel-opens" />
              <FunnelBar label="Completions" value={f.completions} max={f.opens} color="var(--accent)" testid="pg-funnel-completions" />
              <FunnelBar label="Signup clicks" value={f.signup_clicks} max={f.opens} color="#7B4DFF" testid="pg-funnel-signup" />
            </div>
          </div>

          {/* Per-topic table */}
          {data.per_topic.length > 0 && (
            <div className="pm-card p-4 mb-6" data-testid="pg-per-topic">
              <h2 className="font-display font-bold text-xl mb-3">Engagement by topic</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ color: "var(--text-muted)" }}>
                    <th className="text-left p-2 font-semibold">Topic</th>
                    <th className="text-right p-2 font-semibold">Opens</th>
                    <th className="text-right p-2 font-semibold">Stage views</th>
                    <th className="text-right p-2 font-semibold">Completions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_topic.map((r) => (
                    <tr key={r.topic_id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`pg-topic-${r.topic_id}`}>
                      <td className="p-2 font-semibold">{r.topic_name}</td>
                      <td className="p-2 text-right">{r.opens}</td>
                      <td className="p-2 text-right">{r.stage_views}</td>
                      <td className="p-2 text-right">{r.completions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recent activity */}
          {data.recent.length > 0 && (
            <div className="pm-card p-4" data-testid="pg-recent">
              <h2 className="font-display font-bold text-xl mb-3">Recent activity</h2>
              <ul className="space-y-1 text-xs">
                {data.recent.map((e) => (
                  <li key={e.id} className="flex gap-3">
                    <span className="w-14 shrink-0 font-semibold" style={{ color: "var(--primary)" }}>{e.event}</span>
                    <span className="flex-1 truncate" style={{ color: "var(--text-secondary)" }}>
                      {e.topic_id || "—"}{e.stage ? ` · ${e.stage}` : ""} · session {e.session_id.slice(0, 8)}
                    </span>
                    <span style={{ color: "var(--text-muted)" }}>{new Date(e.ts).toLocaleTimeString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
